'use client';

/**
 * ObeyaPlanLensFirmnessCard — the Rumsfeld matrix summary: one bar across
 * every requirement (widths from the BFF) with the quote, allocation and
 * purchase-order counts. The selected and open lists are their own cards.
 * A renderer: counts and widths arrive computed; nothing is derived here.
 *
 * @see klyde_edge/ui_config/composition/cards/obeya.py — ObeyaPlanLensFirmnessCard
 * @see klyde_edge/klyde_intelligence/projects/plan_lenses.py
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo } from 'react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { usePlanLenses, money } from './use-plan-lenses';

const SEG = { open: '#8d8f9b', unquoted: '#e0a94f', quoted: '#5fd0a9' };

export function ObeyaPlanLensFirmnessCard() {
  const { contentKey } = useCardIdentity();
  const { mcpId, strategy, d, isLoading, queryKey } = usePlanLenses();

  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_plan_lens_firmness',
    status: (d ? 'loaded' : mcpId && strategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: queryKey,
    text: d ? d.labels.firmness_title : 'Firmness',
    json: d ? d.knowledge : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [contentKey, d, mcpId, strategy]));

  if (!mcpId) return null;
  if (isLoading && !d) return <div className={cn(text.muted, 'p-6 text-center')}>…</div>;
  if (!d) return null;

  const L = d.labels;
  const k = d.knowledge.data;
  const g = k.paint;
  const key = (color: string, label: string, n: number) => (
    <span key={label}>
      <span className="inline-block w-[8px] h-[8px] rounded-sm mr-1.5" style={{ background: color }} />
      {label} <b className="text-foreground tabular-nums">{n}</b>
    </span>
  );

  return (
    <div className="flex flex-col h-full px-6 py-4 gap-3 overflow-auto">
      <div className="flex items-baseline gap-3 flex-wrap">
        <span className={cn(text.eyebrow)}>{L.firmness_title}</span>
        <span className={cn(text.muted)}>{L.firmness_meta}</span>
      </div>

      <div>
        <div className="flex h-3 rounded overflow-hidden bg-muted/40 mb-3">
          <div style={{ width: `${g.selected_quoted_pct ?? 0}%`, background: SEG.quoted }} />
          <div style={{ width: `${g.selected_unquoted_pct ?? 0}%`, background: SEG.unquoted }} />
          <div style={{ width: `${g.open_pct ?? 0}%`, background: SEG.open, opacity: 0.6 }} />
        </div>
        <div className={cn(text.muted, 'flex gap-5 flex-wrap')}>
          {key(SEG.quoted, L.bar_selected_quoted, k.selected_quoted_count)}
          {key(SEG.unquoted, L.bar_selected_unquoted, k.selected_unquoted_count)}
          {key(SEG.open, L.bar_open, k.open_count)}
          <span>{L.fully_allocated} <b className="text-foreground tabular-nums">{k.selected_fully_allocated_count} / {k.selected_count}</b></span>
          {k.committed_po_value != null && (
            <span>{L.purchase_orders} <b className="text-foreground tabular-nums">{money(k.committed_po_value)}</b></span>
          )}
        </div>
      </div>

    </div>
  );
}
